import React, { useEffect, useState } from "react";
import {
  TextField,
  Button, 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Divider,
  InputAdornment,
  IconButton
} from "@mui/material";
import { Search } from "@mui/icons-material"
import SearchIcon from '@mui/icons-material/Search';

import { userRequest } from "../requestMethods"
import WordTranslationComponent from "./WordTranslationComponent";

const WordAddForm = ({ word, isEditing, isDialog, searchEnabled=true, onAcceptFunc, onCancelFunc }) => {

  const [editedWord, setEditedWord] = useState(word);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('')
  const [alertDuration, setAlertDuration] = useState(3000)

  const [dictionaryWord, setDictionaryWord] = useState(null)
  const [searchQuery, setSearchQuery] = useState('');
  const [grammarTopics, setGrammarTopics] = useState([]);
  const [showTopicsList, setShowTopicsList] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  useEffect(() => {
    let timer;
    if (alertMessage) {
      setShowAlert(true);
      timer = setTimeout(() => {
        setShowAlert(false);
        if (alertMessage !== "")
          setAlertMessage("")
      }, alertDuration);
    }
    return () => clearTimeout(timer);
  }, [alertMessage, alertDuration]);

  useEffect(() => {
    setEditedWord(word)
    setDictionaryWord(null)
  }, [word])

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onCancelFunc()
    }
  }

  const handleChange = (e) => {
    let { id, value } = e.target;

    if (editedWord) {
      setEditedWord({ ...editedWord, [id]: value });
    }
  };

  const getValidationErrors = () => {
    const errors = [];
    if (!editedWord?.word?.trim()) {
      errors.push('Word is required');
    }
    if (!editedWord?.pos?.trim()) {
      errors.push('Part of speech (pos) is required - e.g. NOUN, VERB, ADJ');
    }
    return errors;
  };

  const handleOkButton = (e) => {
    const errors = getValidationErrors();
    if (errors.length > 0) {
      setAlertMessage(errors.join('. '));
      setAlertDuration(5000);
      return;
    }
    onAcceptFunc(editedWord);
  }

  const handleSearch = async (e) => {
    try {
      let res = await userRequest.get(`/dictionaryWord/search/${editedWord.word}`)
      let data = res.data
      
      if (!data || data.exists === false) {
        setAlertMessage("Not found")
        setDictionaryWord(null)
        return
      }
      else {
        let result = data.dictionaryWord
        setDictionaryWord({ ...result})
      }
    } catch (err) {
      console.log('Search dictionaryWord error: ' + err)
    }
  }

  const setTranslation = (trArray) => {
    setEditedWord({ ...editedWord, translation: trArray.join(', '), globalID: dictionaryWord._id})
  }

  const handleSearchGrammar = async () => {
    try {
      let response;
      if (searchQuery.trim() === '') {
        // If search is empty, fetch all topics
        response = await userRequest.get('/grammar');
      } else {
        // Search by tag
        response = await userRequest.get(`/grammar/tags/${searchQuery}`);
      }
      
      setGrammarTopics(response.data);
      setShowTopicsList(true);
      setSearchPerformed(true);
    } catch (err) {
      console.error('Error searching grammar topics:', err);
    }
  };

  const handleSelectTopic = (topic) => {
    setEditedWord({
      ...editedWord,
      linkTitle: topic.title,
      link: `/grammar/topic/${topic.slug}`
    });
    setShowTopicsList(false);
  };

  const mainBody = () => {
    return (
      <table>
        <tbody>
        <tr><td>
        <Alert severity="error" sx={{ display: showAlert ? 'block' : 'none', animation: 'flashing 1s linear infinite' }}>
          {alertMessage}
        </Alert>


        <Grid container spacing={2} alignItems="center">
          <Grid item>
        <TextField
          id="word"
          label="Word"
          value={editedWord ? editedWord.word : ''}
          onChange={handleChange}
          margin="normal"
          variant="outlined" />
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={handleSearch}>
              <Search />
            </Button>
          </Grid>
        </Grid>

        <TextField
          id="pos"
          label="Part of speech (pos) *"
          value={editedWord ? editedWord.pos : ''}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          required
          helperText="Required. e.g. NOUN, VERB, ADJ, ADV" />
          <br/>

        <TextField
          id="translation"
          label="Translation"
          value={editedWord ? editedWord.translation : ''}
          onChange={handleChange}
          margin="normal"
          variant="outlined" />
          <br/>

        <TextField
          id="comment"
          label="Comment"
          value={editedWord ? editedWord.comment : ''}
          onChange={handleChange}
          margin="normal"
          variant="outlined" />
        <br />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={10}>
            <TextField
              id="linkTitle"
              label="Grammar topic"
              value={editedWord && editedWord.linkTitle ? editedWord.linkTitle : ''}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={2}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => setShowTopicsList(!showTopicsList)}
              sx={{ mt: 2 }}
            >
              <SearchIcon />
            </Button>
          </Grid>
        </Grid>

        {showTopicsList && (
          <>
            <TextField
              fullWidth
              label="Search Grammar Topics"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchGrammar();
                }
              }}
              margin="normal"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearchGrammar}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Paper elevation={2} sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
              {grammarTopics.length > 0 ? (
                <List dense>
                  {grammarTopics.map((topic) => (
                    <React.Fragment key={topic._id}>
                      <ListItem 
                        button 
                        onClick={() => handleSelectTopic(topic)}
                      >
                        <ListItemText 
                          primary={topic.title} 
                          secondary={topic.summary} 
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                searchPerformed && (
                  <Typography sx={{ p: 2, textAlign: 'center' }}>
                    No grammar topics found
                  </Typography>
                )
              )}
            </Paper>
          </>
        )}

        <TextField
          id="link"
          label="Grammar rule/Link"
          value={editedWord && editedWord.link ? editedWord.link : ''}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          fullWidth
        />

        <Button variant="contained" color="primary" onClick={handleOkButton}>
          {isEditing ? "Save" : "Add"}
        </Button>
        <Button variant="contained" onClick={onCancelFunc}>
          Cancel
        </Button>

        <br /> <br />
        </td>
        <td>
          {dictionaryWord && <WordTranslationComponent word={dictionaryWord} wordSetter={setTranslation}></WordTranslationComponent>}
        </td>
        </tr>
        </tbody>
      </table>
    )
  }

  if (isDialog) {
    return (
      <Dialog open={true} onClose={handleChange} onKeyDown={handleKeyDown}>
        <DialogTitle>Add word "{word.word}" </DialogTitle>
        <DialogContent>
          {mainBody()}
        </DialogContent>
      </Dialog>
    )
  }
  // else 

  return (
    <div>
      {mainBody()}
    </div>

  );
};

export default WordAddForm